// Static content for the grooh marketing site
import {
  DoorOpen, ReceiptText, MessageSquareWarning, Users2, CalendarCheck2,
  Megaphone, ShieldCheck, Wallet, Vote, Car, PackageCheck, BellRing
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Pricing", to: "/pricing" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export const STATS = [
  { value: "2,400+", label: "Societies onboarded" },
  { value: "1.8M", label: "Residents connected" },
  { value: "₹640Cr", label: "Dues collected" },
  { value: "99.98%", label: "Uptime guarantee" },
];

export const MARQUEE_WORDS = [
  "Visitor Management", "Maintenance Billing", "Community Feed", "Amenity Booking",
  "Complaint Tracking", "Digital Notices", "Gate Security", "Online Payments",
];

export const FEATURES = [
  {
    icon: DoorOpen,
    title: "Visitor & Gate Management",
    desc: "Pre-approve guests, scan QR passes at the gate, and track every entry in real time with photo logs.",
    span: "md:col-span-7",
  },
  {
    icon: ReceiptText,
    title: "Maintenance Billing",
    desc: "Auto-generate invoices, split charges, and reconcile payments without a single spreadsheet.",
    span: "md:col-span-5",
  },
  {
    icon: MessageSquareWarning,
    title: "Complaint Tracking",
    desc: "Residents raise tickets, committees assign them, everyone sees the status. No more lost WhatsApp messages.",
    span: "md:col-span-5",
  },
  {
    icon: CalendarCheck2,
    title: "Amenity Booking",
    desc: "Clubhouse, gym, courts — booked in a tap with conflict-free slots and automatic reminders.",
    span: "md:col-span-7",
  },
  {
    icon: Wallet,
    title: "Online Payments",
    desc: "UPI, cards, netbanking. Residents pay dues in seconds, treasurers get instant reconciliation.",
    span: "md:col-span-4",
  },
  {
    icon: Megaphone,
    title: "Notices & Community Feed",
    desc: "Broadcast circulars, run polls, and keep the whole community in one lively, moderated feed.",
    span: "md:col-span-4",
  },
  {
    icon: ShieldCheck,
    title: "Bank-grade Security",
    desc: "Role-based access, encrypted data, and audit trails for every financial action.",
    span: "md:col-span-4",
  },
];

export const MANIFESTO = [
  {
    no: "01",
    title: "Communities deserve better than group chats.",
    body: "Running a society on scattered WhatsApp groups and paper registers is chaos. grooh gives your committee one calm, organised home for everything.",
  },
  {
    no: "02",
    title: "Money should be transparent, not mysterious.",
    body: "Every rupee collected and spent is visible to the residents who fund it. Automated books, instant receipts, and reports that actually add up.",
  },
  {
    no: "03",
    title: "Security starts at the gate.",
    body: "Know exactly who is inside your gates, when, and why. Digital passes, guard apps, and instant alerts protect the people who matter.",
  },
  {
    no: "04",
    title: "Great software feels invisible.",
    body: "No training manuals. No steep learning curve. grooh is built so a first-time smartphone user and a tech-savvy teenager both feel at home.",
  },
];

export const TESTIMONIALS = [
  {
    quote: "We collected 96% of dues in the first month — up from 61%. The committee finally sleeps at night.",
    name: "Rajesh Menon",
    role: "Secretary, Palm Meadows",
    avatar: "https://images.unsplash.com/photo-1757744705465-ea08b0ddc38a",
  },
  {
    quote: "Visitor entry used to be a register and a pen. Now it's a QR code. Residents feel genuinely safer.",
    name: "Ananya Iyer",
    role: "President, Lakeview Heights",
    avatar: "https://images.unsplash.com/photo-1609371497456-3a55a205d5eb",
  },
  {
    quote: "The complaint tracker alone was worth it. Nothing slips through the cracks anymore.",
    name: "Vikram Shah",
    role: "Treasurer, Orchid Residency",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d",
  },
];

export const PRICING = [
  {
    name: "Starter",
    price: "₹0",
    period: "/forever",
    tagline: "For small societies finding their feet.",
    featured: false,
    features: [
      "Up to 50 units",
      "Community feed & notices",
      "Complaint tracking",
      "Basic visitor log",
      "Email support",
    ],
    cta: "Start free",
  },
  {
    name: "Growth",
    price: "₹6",
    period: "/unit / month",
    tagline: "The complete toolkit for thriving communities.",
    featured: true,
    features: [
      "Unlimited units",
      "Everything in Starter",
      "Maintenance billing & online payments",
      "QR visitor & gate management",
      "Amenity booking + polls",
      "Priority support",
    ],
    cta: "Get started",
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    tagline: "For large gated townships & developers.",
    featured: false,
    features: [
      "Everything in Growth",
      "Multi-society dashboard",
      "Dedicated account manager",
      "Custom integrations & API",
      "On-ground onboarding",
    ],
    cta: "Talk to sales",
  },
];

export const FAQS = [
  {
    q: "How long does it take to onboard our society?",
    a: "Most societies are fully live within 48 hours. We import your unit list, resident data and opening balances, and our team hosts a free walkthrough for your committee.",
  },
  {
    q: "Do residents need to be tech-savvy to use grooh?",
    a: "Not at all. grooh is designed for everyone — from first-time smartphone users to teenagers. Residents can also use WhatsApp and SMS notifications alongside the app.",
  },
  {
    q: "Is our financial data secure?",
    a: "Yes. We use bank-grade encryption, role-based access control, and maintain a full audit trail of every financial action. Payments are processed through PCI-DSS compliant gateways.",
  },
  {
    q: "Can we switch from our current provider?",
    a: "Absolutely. Our migration team handles data transfer end-to-end at no extra cost, so you move without losing a single record.",
  },
  {
    q: "What does the pricing include?",
    a: "All plans include unlimited notices, community feed, and complaint tracking. Growth adds billing, payments and gate management. There are no hidden setup fees.",
  },
];

export const BLOG_POSTS = [
  {
    slug: "collect-society-maintenance-faster",
    title: "7 proven ways to collect society maintenance faster",
    excerpt: "Chasing dues drains your committee's energy. Here are seven tactics that consistently push collection rates above 95%.",
    category: "Finance",
    date: "Dec 12, 2025",
    read: "6 min read",
    cover: "https://images.unsplash.com/photo-1624204386084-dd8c05e32226",
    body: [
      "Late maintenance payments are the single biggest source of committee stress. The good news: it's almost always a process problem, not a people problem.",
      "1. Automate invoicing. Manual invoices arrive late and inconsistently. Auto-generated bills on a fixed date set clear expectations.",
      "2. Offer one-tap online payments. Every extra step in the payment journey drops collection. UPI and cards remove friction entirely.",
      "3. Send gentle, scheduled reminders. A polite nudge three days before the due date works better than an angry notice three weeks after.",
      "4. Make dues transparent. When residents can see exactly what they owe and why, disputes drop dramatically.",
      "5. Publish where the money goes. Sharing expense reports builds trust and makes residents far more willing to pay on time.",
      "6. Reward early payers. A small recognition in the community feed nudges behaviour more than penalties.",
      "7. Give treasurers real-time dashboards. When you can see collection status at a glance, you act early instead of scrambling at year-end.",
    ],
  },
  {
    slug: "modern-visitor-management",
    title: "Why paper visitor registers are a security risk",
    excerpt: "The humble gate register is a data leak and a safety gap rolled into one. Here's what modern societies do instead.",
    category: "Security",
    date: "Dec 05, 2025",
    read: "5 min read",
    cover: "https://images.unsplash.com/photo-1691256676376-357c3aa66c89",
    body: [
      "A paper register at the gate exposes every resident's name, flat number and movements to anyone who flips a page. That's a privacy problem waiting to happen.",
      "Digital visitor management fixes this by keeping records private, searchable and tamper-proof.",
      "Pre-approvals let residents authorise guests before they arrive, so the guard simply scans a QR code — no phone calls, no waiting.",
      "Photo capture and instant alerts mean the committee always knows who is inside the gates and why.",
      "Best of all, everything is logged automatically. If there's ever an incident, you have an accurate, timestamped trail in seconds.",
    ],
  },
  {
    slug: "building-a-connected-community",
    title: "Building a genuinely connected community",
    excerpt: "Technology can bring neighbours together — if you use it right. A playbook for a warmer, more engaged society.",
    category: "Community",
    date: "Nov 28, 2025",
    read: "7 min read",
    cover: "https://images.unsplash.com/photo-1625283518288-00362afc8663",
    body: [
      "A society is more than a collection of flats — it's a community. But community doesn't happen by accident.",
      "Start with a lively, moderated feed where residents can share, celebrate and organise. Keep it positive with clear guidelines.",
      "Use polls for real decisions — from festival budgets to new amenities. When people vote, they feel ownership.",
      "Celebrate the small things: welcome new residents, wish birthdays, highlight volunteers. Recognition is glue.",
      "Finally, make participation effortless. The easier it is to engage, the more your community will show up.",
    ],
  },
  {
    slug: "committee-handover-checklist",
    title: "The stress-free committee handover checklist",
    excerpt: "New committee, clean slate. Use this checklist to hand over without losing a single document or dispute.",
    category: "Governance",
    date: "Nov 20, 2025",
    read: "4 min read",
    cover: "https://images.unsplash.com/photo-1695973056909-67189edc1c9e",
    body: [
      "Committee handovers are notorious for lost passwords, missing files and unfinished disputes. It doesn't have to be that way.",
      "Keep all documents in one shared, permission-controlled vault so nothing walks out the door with an outgoing member.",
      "Ensure open complaints and pending payments are visible to the incoming team from day one.",
      "Transfer roles digitally with the click of a button instead of sharing personal logins.",
      "With everything in grooh, a handover takes an afternoon — not a fraught month.",
    ],
  },
];
